/** @format */

import {forwardRef, useEffect, useMemo, useState} from 'react';

import {Box} from '@mui/material';

import {CalcMethod, WidgetStat} from 'components/PropertyWidget/model';

import MultilineChart from 'components/charts/multiline-chart';

import {MultilineChartInterface} from 'components/charts/multiline-chart/model/common';

import {ValueBox} from 'components/PropertyWidget/style';

import WidgetFormattedNumber from 'components/PropertyWidget/components/WidgetFormattedNumber';

import {Discreteness} from 'components/PeriodSelector/model';

import {AnalyticGoalFromQuery, FunnelGoalStatFromQuery} from '../../models/api';
import {ChartIds} from '../../models/chartIds';
import {GraphComplexValuesList} from '../../models/common';
import {Space, WidgetConditionsList} from '../../models/spaces';
import {WidgetBlock} from '../../models/widgets';
import WidgetsWrap from '../widgets-wrap';
import computeBasicCrStats from './helpers/computeBasicCrStats';
import computeCrStats from './helpers/computeCrStats';
import computeMixedCrStats from './helpers/computeMixedCrStats';
import getChartTypeSettings from './helpers/getChartTypeSettings';
import getHeightParams from './helpers/getHeightParams';
import {getSafeCrName} from './helpers/getSafeCrName';
import restoreSelectedCharts from './helpers/restoreSelectedCharts';
import reworkTooltipData from './helpers/reworkTooltipData';
import updateSelectedCharts from './helpers/updateSelectedCharts';
import updateSpaceTitle from './helpers/updateSpaceTitle';
import updateToggleChart from './helpers/updateToggleChart';
import updateWidgetCustom from './helpers/updateWidgetCustom';
import updateWidgetMethod from './helpers/updateWidgetMethod';
import {DashboardRoot as Root, dashboardClasses as classes} from './style';
import {needUpdateCrStats} from './utils';
import {getChartsDataWithCR} from './helpers/getChartsDataWithCR';
import {updateScale} from './helpers/updateScale';
import {getChartScaleSettings} from './helpers/getChartScaleSettings';
import {addFakeForecast} from '../../helpers/addFakeForecast';
import isStubChartId from '../../helpers/isStubChartId';
import StubLayer from '../stub-layer';
import {NoteOpenProvider} from '../../contexts/NoteOpenContext';

interface Props {
  space: Space;
  widgetBlocks: WidgetBlock[];
  chartsData: MultilineChartInterface<ChartIds>;
  widgetConditions: WidgetConditionsList;
  analyticGoals: ReadonlyArray<AnalyticGoalFromQuery>;
  funnelGoalStats: ReadonlyArray<FunnelGoalStatFromQuery>;
  graphComplexValues: GraphComplexValuesList;
  saveSpaceSettings: (space: Space) => void;
  onRemoveSpace: () => void;
  onWidgetChange: (widgetId: ChartIds) => void;
  onWorkspaceChange: () => void;
  zIndex: number;
  rechartsDates: string[];
  discreteness: Discreteness;
}

const Dashboard = forwardRef(
  (
    {
      space: defaultSpace,
      widgetBlocks,
      chartsData,
      widgetConditions,
      analyticGoals,
      funnelGoalStats,
      graphComplexValues,
      saveSpaceSettings,
      onRemoveSpace,
      onWidgetChange,
      onWorkspaceChange,
      zIndex,
      rechartsDates,
      discreteness,
    }: Props,
    ref,
  ) => {
    const [space, setSpace] = useState<Space>(defaultSpace);

    useEffect(() => {
      const ids = Object.keys(space.widgetsSettings);
      const crIds = ids.filter(id => id.startsWith('funnel_cr_')) as ChartIds[];
      if (!crIds.length) return;

      crIds.forEach(widgetId => {
        const ws = space.widgetsSettings[widgetId];
        const custom = (ws?.custom || {}) as {
          aGoalId?: number | string;
          bGoalId?: number | string;
          label?: string;
        };
        if (!custom.aGoalId || !custom.bGoalId) return;

        const isABasicMetric =
          custom.aGoalId === 'clicks' ||
          custom.aGoalId === 'impressions' ||
          custom.aGoalId === 'shows';
        const isBBasicMetric =
          custom.bGoalId === 'clicks' ||
          custom.bGoalId === 'impressions' ||
          custom.bGoalId === 'shows';
        const isBothBasicMetric = isABasicMetric && isBBasicMetric;

        let goalA: string;
        let goalB: string;
        let label: string;
        let stats: WidgetStat[];

        // Определяем названия для каждого ID отдельно
        goalA =
          analyticGoals.find(g => String(g.id) === String(custom.aGoalId))?.name ||
          getSafeCrName(String(custom.aGoalId));
        goalB =
          analyticGoals.find(g => String(g.id) === String(custom.bGoalId))?.name ||
          getSafeCrName(String(custom.bGoalId));

        label = `CR ${goalA}/${goalB}, %`;

        // Обновлять только при реальной необходимости, чтобы не триггерить бесконечные сохранения
        const needUpdate = needUpdateCrStats({
          prevLabel: ws?.custom?.label,
          prevCustom: ws?.custom,
          custom,
          label,
          prevStats: ws?.stats,
          funnelGoalStats,
          graphComplexValuesKeysCount: Object.keys(graphComplexValues).length,
        });

        if (!needUpdate) return;

        if (isBothBasicMetric) {
          const {yesterday, min, max, dynamics} = computeBasicCrStats(
            graphComplexValues,
            custom.aGoalId as 'clicks' | 'impressions',
            custom.bGoalId as 'clicks' | 'impressions',
          );
          stats = [
            {
              heading: 'Конверсия за вчера',
              ValueComponent: (
                <ValueBox>
                  <WidgetFormattedNumber number={yesterday} roundNumberBy={1} postfix='%' />
                </ValueBox>
              ),
              showConditions: ['all'],
            },
            {
              heading: 'Мин',
              ValueComponent: (
                <ValueBox>
                  <WidgetFormattedNumber number={min} roundNumberBy={1} postfix='%' />
                </ValueBox>
              ),
              showConditions: ['all'],
            },
            {
              heading: 'Макс',
              ValueComponent: (
                <ValueBox>
                  <WidgetFormattedNumber number={max} roundNumberBy={1} postfix='%' />
                </ValueBox>
              ),
              showConditions: ['all'],
            },
            {
              heading: 'Динамика',
              ValueComponent: (
                <ValueBox>
                  <WidgetFormattedNumber
                    number={dynamics}
                    roundNumberBy={1}
                    postfix=' п.п.'
                    showPlusMinus
                    colorize
                  />
                </ValueBox>
              ),
              showConditions: ['all'],
            },
          ];
        } else if (!isABasicMetric && !isBBasicMetric) {
          // Оба ID - аналитические цели
          const {yesterday, min, max, dynamics} = computeCrStats(
            funnelGoalStats,
            Number(custom.aGoalId),
            Number(custom.bGoalId),
          );

          stats = [
            {
              heading: 'Конверсия за вчера',
              ValueComponent: (
                <ValueBox>
                  <WidgetFormattedNumber number={yesterday} roundNumberBy={1} postfix='%' />
                </ValueBox>
              ),
              showConditions: ['all'],
            },
            {
              heading: 'Мин',
              ValueComponent: (
                <ValueBox>
                  <WidgetFormattedNumber number={min} roundNumberBy={1} postfix='%' />
                </ValueBox>
              ),
              showConditions: ['all'],
            },
            {
              heading: 'Макс',
              ValueComponent: (
                <ValueBox>
                  <WidgetFormattedNumber number={max} roundNumberBy={1} postfix='%' />
                </ValueBox>
              ),
              showConditions: ['all'],
            },
            {
              heading: 'Динамика',
              ValueComponent: (
                <ValueBox>
                  <WidgetFormattedNumber
                    number={dynamics}
                    roundNumberBy={1}
                    postfix=' п.п.'
                    showPlusMinus
                    colorize
                  />
                </ValueBox>
              ),
              showConditions: ['all'],
            },
          ];
        } else {
          // Смешанный случай: один ID - базовый показатель, другой - аналитическая цель.
          // Нормализуем порядок: числитель = аналитическая цель, знаменатель = базовая метрика
          const numeratorKey = !isABasicMetric ? Number(custom.aGoalId) : Number(custom.bGoalId);
          const denominatorKey = isABasicMetric
            ? custom.aGoalId === 'shows'
              ? 'impressions'
              : (custom.aGoalId as 'clicks' | 'impressions')
            : custom.bGoalId === 'shows'
              ? 'impressions'
              : (custom.bGoalId as 'clicks' | 'impressions');

          const {yesterday, min, max, dynamics} = computeMixedCrStats(
            graphComplexValues,
            funnelGoalStats,
            numeratorKey as number,
            denominatorKey as 'clicks' | 'impressions',
          );

          stats = [
            {
              heading: 'Конверсия за вчера',
              ValueComponent: (
                <ValueBox>
                  <WidgetFormattedNumber number={yesterday} roundNumberBy={1} postfix='%' />
                </ValueBox>
              ),
              showConditions: ['all'],
            },
            {
              heading: 'Мин',
              ValueComponent: (
                <ValueBox>
                  <WidgetFormattedNumber number={min} roundNumberBy={1} postfix='%' />
                </ValueBox>
              ),
              showConditions: ['all'],
            },
            {
              heading: 'Макс',
              ValueComponent: (
                <ValueBox>
                  <WidgetFormattedNumber number={max} roundNumberBy={1} postfix='%' />
                </ValueBox>
              ),
              showConditions: ['all'],
            },
            {
              heading: 'Динамика',
              ValueComponent: (
                <ValueBox>
                  <WidgetFormattedNumber
                    number={dynamics}
                    roundNumberBy={1}
                    postfix=' п.п.'
                    showPlusMinus
                    colorize
                  />
                </ValueBox>
              ),
              showConditions: ['all'],
            },
          ];
        }

        const crHash = `${funnelGoalStats.length}|${Object.keys(graphComplexValues).length}`;

        setSpace(prev =>
          updateWidgetCustom({
            data: prev,
            widgetId,
            custom: {...custom, label, crComputedHash: crHash},
            stats,
            saveSpaceSettings,
          }),
        );
      });
    }, [
      space.widgetsSettings,
      funnelGoalStats,
      analyticGoals,
      graphComplexValues,
      saveSpaceSettings,
    ]);

    const handleChangeTitle = (value: string) => {
      setSpace(prev =>
        updateSpaceTitle({
          space: prev,
          value,
          saveSpaceSettings,
        }),
      );
    };

    const handleSwitcherClick = (widgetId: ChartIds) => {
      onWidgetChange(widgetId);
      setSpace(prev =>
        updateToggleChart({
          data: prev,
          widgetId,
          saveSpaceSettings,
        }),
      );
    };

    const handleMethodClick = (widgetId: ChartIds, method: CalcMethod) => {
      onWidgetChange(widgetId);
      setSpace(prev =>
        updateWidgetMethod({
          data: prev,
          widgetId,
          method,
          saveSpaceSettings,
        }),
      );
    };

    const handleScaleClick = (widgetId: ChartIds, scale: boolean) => {
      onWidgetChange(widgetId);
      setSpace(prev =>
        updateScale({
          data: prev,
          widgetId,
          scale,
          saveSpaceSettings,
        }),
      );
    };

    const handleCrConfirm = (widgetId: ChartIds, aId: number | string, bId: number | string) => {
      const isABasicMetric = aId === 'clicks' || aId === 'impressions' || aId === 'shows';
      const isBBasicMetric = bId === 'clicks' || bId === 'impressions' || bId === 'shows';
      const isBothBasicMetric = isABasicMetric && isBBasicMetric;

      let goalA: string;
      let goalB: string;
      let label: string;
      let stats: WidgetStat[];

      // Определяем названия для каждого ID отдельно
      goalA = isABasicMetric
        ? aId === 'clicks'
          ? 'Клики'
          : 'Показы'
        : analyticGoals.find(g => String(g.id) === String(aId))?.name || getSafeCrName(String(aId));

      goalB = isBBasicMetric
        ? bId === 'clicks'
          ? 'Клики'
          : 'Показы'
        : analyticGoals.find(g => String(g.id) === String(bId))?.name || getSafeCrName(String(bId));
      label = `CR ${goalA}/${goalB}, %`;

      if (isBothBasicMetric) {
        const {yesterday, min, max, dynamics} = computeBasicCrStats(
          graphComplexValues,
          aId as 'clicks' | 'impressions',
          bId as 'clicks' | 'impressions',
        );
        stats = [
          {
            heading: 'Конверсия за вчера',
            ValueComponent: (
              <ValueBox>
                <WidgetFormattedNumber number={yesterday} roundNumberBy={1} postfix='%' />
              </ValueBox>
            ),
            showConditions: ['all'],
          },
          {
            heading: 'Мин',
            ValueComponent: (
              <ValueBox>
                <WidgetFormattedNumber number={min} roundNumberBy={1} postfix='%' />
              </ValueBox>
            ),
            showConditions: ['all'],
          },
          {
            heading: 'Макс',
            ValueComponent: (
              <ValueBox>
                <WidgetFormattedNumber number={max} roundNumberBy={1} postfix='%' />
              </ValueBox>
            ),
            showConditions: ['all'],
          },
          {
            heading: 'Динамика',
            ValueComponent: (
              <ValueBox>
                <WidgetFormattedNumber
                  number={dynamics}
                  roundNumberBy={1}
                  postfix=' п.п.'
                  showPlusMinus
                  colorize
                />
              </ValueBox>
            ),
            showConditions: ['all'],
          },
        ];
      } else if (!isABasicMetric && !isBBasicMetric) {
        // Оба ID - аналитические цели
        const {yesterday, min, max, dynamics} = computeCrStats(
          funnelGoalStats,
          aId as number,
          bId as number,
        );
        stats = [
          {
            heading: 'Конверсия за вчера',
            ValueComponent: (
              <ValueBox>
                <WidgetFormattedNumber number={yesterday} roundNumberBy={1} postfix='%' />
              </ValueBox>
            ),
            showConditions: ['all'],
          },
          {
            heading: 'Мин',
            ValueComponent: (
              <ValueBox>
                <WidgetFormattedNumber number={min} roundNumberBy={1} postfix='%' />
              </ValueBox>
            ),
            showConditions: ['all'],
          },
          {
            heading: 'Макс',
            ValueComponent: (
              <ValueBox>
                <WidgetFormattedNumber number={max} roundNumberBy={1} postfix='%' />
              </ValueBox>
            ),
            showConditions: ['all'],
          },
          {
            heading: 'Динамика',
            ValueComponent: (
              <ValueBox>
                <WidgetFormattedNumber
                  number={dynamics}
                  roundNumberBy={1}
                  postfix=' п.п.'
                  showPlusMinus
                  colorize
                />
              </ValueBox>
            ),
            showConditions: ['all'],
          },
        ];
      } else {
        // Смешанный случай: один ID - базовый показатель, другой - аналитическая цель
        const {yesterday, min, max, dynamics} = computeMixedCrStats(
          graphComplexValues,
          funnelGoalStats,
          isABasicMetric ? (aId as 'clicks' | 'impressions') : (aId as number),
          isBBasicMetric ? (bId as 'clicks' | 'impressions') : (bId as number),
        );

        stats = [
          {
            heading: 'Конверсия за вчера',
            ValueComponent: (
              <ValueBox>
                <WidgetFormattedNumber number={yesterday} roundNumberBy={1} postfix='%' />
              </ValueBox>
            ),
            showConditions: ['all'],
          },
          {
            heading: 'Мин',
            ValueComponent: (
              <ValueBox>
                <WidgetFormattedNumber number={min} roundNumberBy={1} postfix='%' />
              </ValueBox>
            ),
            showConditions: ['all'],
          },
          {
            heading: 'Макс',
            ValueComponent: (
              <ValueBox>
                <WidgetFormattedNumber number={max} roundNumberBy={1} postfix='%' />
              </ValueBox>
            ),
            showConditions: ['all'],
          },
          {
            heading: 'Динамика',
            ValueComponent: (
              <ValueBox>
                <WidgetFormattedNumber
                  number={dynamics}
                  roundNumberBy={1}
                  postfix=' п.п.'
                  showPlusMinus
                  colorize
                />
              </ValueBox>
            ),
            showConditions: ['all'],
          },
        ];
      }
      setSpace(prev => {
        const updatedSpace = {
          ...prev,
          widgetsSettings: {
            ...prev.widgetsSettings,
            [widgetId]: {
              ...(prev.widgetsSettings[widgetId] || {}),
              custom: {
                aGoalId: aId,
                bGoalId: bId,
                aName: goalA,
                bName: goalB,
                label,
              },
              stats,
            },
          },
          widgetsMenuSelected: prev.widgetsMenuSelected.includes(widgetId)
            ? prev.widgetsMenuSelected
            : [...prev.widgetsMenuSelected, widgetId],
          chartSelected: {
            ...prev.chartSelected,
            current: prev.chartSelected.current.includes(widgetId)
              ? prev.chartSelected.current
              : [...prev.chartSelected.current, widgetId],
          },
        };

        saveSpaceSettings(updatedSpace);

        return updatedSpace;
      });
    };

    const handleChartClick = (chartId: ChartIds) => {
      const isSingleClick = space.chartSelected.current.length === 1;

      setSpace(prev => {
        if (isSingleClick) {
          return restoreSelectedCharts({
            data: prev,
            saveSpaceSettings,
          });
        }
        return updateSelectedCharts({
          data: prev,
          widgetIds: [chartId],
          saveSpaceSettings,
        });
      });
    };

    const handleUpdateMenuSettings = (space: Space) => {
      onWorkspaceChange();
      setSpace(space);
      saveSpaceSettings(space);
    };

    const selectedIds = space.chartSelected.current;

    // Локально пересчитываем chartsData с CR виджетами
    const localChartsData = useMemo(() => {
      return getChartsDataWithCR({
        chartsData,
        widgetsSettings: space.widgetsSettings,
        funnelGoalStats,
        widgetBlocks,
        graphComplexValues,
        analyticGoals,
        rechartsDates,
        discreteness,
      });
    }, [
      chartsData,
      space.widgetsSettings,
      funnelGoalStats,
      widgetBlocks,
      graphComplexValues,
      analyticGoals,
      rechartsDates,
      discreteness,
    ]);

    // Фильтруем стабы из расчётов графиков
    const chartSelectedIds = useMemo(
      () => selectedIds.filter(id => !isStubChartId(id)),
      [selectedIds],
    );

    const stubChartIds = useMemo(() => selectedIds.filter(isStubChartId), [selectedIds]);

    const stubColors = useMemo(() => {
      const stubBlock = widgetBlocks.find(b => b.id === 'stubs');
      return stubBlock?.color ?? {};
    }, [widgetBlocks]);

    // Перерасчёт высот графиков

    const {heightParams, mappedData} = useMemo(
      () =>
        getHeightParams({
          data: localChartsData,
          selectedIds: chartSelectedIds,
          stubLayerCount: stubChartIds.length,
        }),
      [localChartsData, chartSelectedIds, stubChartIds.length],
    );

    // Перерасчёт логики тултипов

    const filteredData = useMemo(
      () => reworkTooltipData(mappedData, chartSelectedIds),
      [mappedData, chartSelectedIds],
    );

    // Добавляем фейковый прогноз, см. описание внутри

    const multilineChartData = useMemo(() => addFakeForecast(filteredData), [filteredData]);

    return (
      <NoteOpenProvider>
        <Root sx={{zIndex}} data-testid={`dc-dashboard-${space.id}`} ref={ref}>
          <Box className={classes.content}>
            <WidgetsWrap
              space={space}
              widgetBlocks={widgetBlocks}
              widgetConditions={widgetConditions}
              analyticGoals={analyticGoals}
              onCrConfirm={(widgetId, aId, bId) => handleCrConfirm(widgetId, aId, bId)}
              onChangeTitle={handleChangeTitle}
              onSwitcherClick={handleSwitcherClick}
              onMethodClick={handleMethodClick}
              onScaleClick={handleScaleClick}
              onConfirmMenuSettings={handleUpdateMenuSettings}
              onRemoveSpace={onRemoveSpace}
            />
            <MultilineChart
              data={multilineChartData}
              heightParams={heightParams}
              syncId={space.id}
              onChartClick={handleChartClick}
              selectedChartIds={chartSelectedIds}
              chartTypeSettings={getChartTypeSettings(space)}
              chartScaleSettings={getChartScaleSettings(space)}
            >
              {stubChartIds.map(id => (
                <StubLayer key={id} chartId={id} color={stubColors[id] ?? '#fff'} />
              ))}
            </MultilineChart>
          </Box>

          <Box className={classes.borderBox} />
        </Root>
      </NoteOpenProvider>
    );
  },
);

Dashboard.displayName = 'Dashboard';

export default Dashboard;
